import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Filter, X, SlidersHorizontal, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline waitlist form used by the Babies "Coming Soon" hero
function ComingSoonWaitlist() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/waitlist`, { email, category: 'babies' });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="border-2 border-[#0A0A0A] bg-[#D5FDD8] px-5 py-4 font-semibold" data-testid="babies-waitlist-success">
        You're on the list — we'll ping you the second the Doe Wiz Babies drop goes live.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-lg" data-testid="babies-waitlist-form">
      <input
        type="email"
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="flex-1 border-2 border-[#0A0A0A] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FAD4D4]"
        required
        data-testid="babies-waitlist-email"
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className="border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white px-6 py-3 text-sm font-black tracking-wider uppercase hover:bg-[#FAD4D4] hover:text-[#0A0A0A] transition-colors disabled:opacity-60"
        data-testid="babies-waitlist-submit"
      >
        {status === 'loading' ? 'Adding…' : 'Notify Me'}
      </button>
    </form>
  );
}

const categoryOptions = [
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
  { value: 'footwear', label: 'Footwear' },
  { value: 'boys', label: 'Boys' },
  { value: 'girls', label: 'Girls' },
  { value: 'babies', label: 'Babies' },
  { value: 'hats', label: 'Hats' },
  { value: 'accessories', label: 'Accessories' },
];

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const category = searchParams.get('category');
  const subcategory = searchParams.get('subcategory');
  const brand = searchParams.get('brand');
  const search = searchParams.get('search');
  const featured = searchParams.get('featured');
  const promo = searchParams.get('promo');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (subcategory) params.append('subcategory', subcategory);
        if (brand) params.append('brand', brand);
        if (search) params.append('search', search);
        if (featured) params.append('featured', featured);
        if (promo) params.append('promo', promo);

        const { data } = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, subcategory, brand, search, featured, promo]);

  const handleCategoryChange = (value, checked) => {
    if (checked) {
      setSearchParams({ category: value });
    } else {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('category');
      setSearchParams(newParams);
    }
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setSearchParams({});
    setFilterOpen(false);
  };

  const activeFiltersCount = [category, search, featured, promo].filter(Boolean).length;

  const getPageTitle = () => {
    if (brand === 'timberland') return 'Timberland';
    if (brand === 'nike') return 'Nike & Jordan';
    if (search) return `Search: "${search}"`;
    if (category) return categoryOptions.find(c => c.value === category)?.label || category.charAt(0).toUpperCase() + category.slice(1);
    if (featured === 'true') return 'Featured Products';
    if (promo === 'true') return 'Sale Items';
    return 'All Products';
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Categories */}
      <div>
        <h4 className="font-semibold mb-4 uppercase text-sm tracking-wider">Categories</h4>
        <div className="space-y-3">
          {categoryOptions.map((cat) => (
            <div key={cat.value} className="flex items-center space-x-3">
              <Checkbox
                id={cat.value}
                checked={category === cat.value}
                onCheckedChange={(checked) => handleCategoryChange(cat.value, checked)}
                className="border-2 border-[#0A0A0A] data-[state=checked]:bg-[#0A0A0A]"
              />
              <label htmlFor={cat.value} className="text-sm cursor-pointer">
                {cat.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Filters */}
      <div>
        <h4 className="font-semibold mb-4 uppercase text-sm tracking-wider">Quick Filters</h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="featured"
              checked={featured === 'true'}
              onCheckedChange={(checked) => {
                const newParams = new URLSearchParams(searchParams);
                if (checked) {
                  newParams.set('featured', 'true');
                } else {
                  newParams.delete('featured');
                }
                setSearchParams(newParams);
              }}
              className="border-2 border-[#0A0A0A] data-[state=checked]:bg-[#0A0A0A]"
            />
            <label htmlFor="featured" className="text-sm cursor-pointer">
              Featured Only
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <Checkbox
              id="promo"
              checked={promo === 'true'}
              onCheckedChange={(checked) => {
                const newParams = new URLSearchParams(searchParams);
                if (checked) {
                  newParams.set('promo', 'true');
                } else {
                  newParams.delete('promo');
                }
                setSearchParams(newParams);
              }}
              className="border-2 border-[#0A0A0A] data-[state=checked]:bg-[#0A0A0A]"
            />
            <label htmlFor="promo" className="text-sm cursor-pointer">
              On Sale
            </label>
          </div>
        </div>
      </div>

      {activeFiltersCount > 0 && (
        <Button
          onClick={clearFilters}
          variant="outline"
          data-testid="clear-filters"
          className="w-full border-2 border-[#0A0A0A] rounded-none hover:bg-[#FAD4D4]"
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );

  // Babies "Coming Soon" hero — renders when the whole category is a coming-soon teaser
  const isBabiesComingSoon = category === 'babies' && !loading && products.length > 0 && products.every((p) => p.is_coming_soon);
  if (isBabiesComingSoon) {
    const hero = products[0];
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        {/* Breadcrumb */}
        <div className="border-b-2 border-[#0A0A0A] bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/" className="text-[#525252] hover:text-[#0A0A0A]">Home</Link>
              <span className="text-[#525252]">/</span>
              <span className="font-semibold">Babies</span>
            </nav>
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16" data-testid="babies-coming-soon-hero">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            {/* Image */}
            <div className="relative border-2 border-[#0A0A0A] card-brutalist overflow-hidden aspect-[4/5] bg-[#F5E6D3]">
              <img
                src={hero.images?.[0]?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${hero.images[0]}` : hero.images?.[0]}
                alt="Doe Wiz Babies — Coming Soon"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute top-4 left-4 bg-[#FAD4D4] border-2 border-[#0A0A0A] px-3 py-1.5 text-xs font-black tracking-widest">
                COMING SOON
              </div>
            </div>

            {/* Copy */}
            <div className="space-y-6">
              <span className="text-xs uppercase tracking-[0.3em] font-semibold text-[#525252]" data-testid="babies-eyebrow">
                Doe Wiz Babies · The Little Boss Drop
              </span>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="babies-hero-title"
              >
                MINI DOE WIZ.
                <br />
                <span className="text-[#0A0A0A]/60">COMING SOON.</span>
              </h1>
              <p className="text-base sm:text-lg text-[#333] leading-relaxed max-w-lg">
                A hand-curated capsule for baby girls & boys — soft onesies, tiny caps, matching sets, and the whole nursery hustle-kit. Same Doe Wiz energy, mini-sized. Sign up and be first through the door on drop day.
              </p>

              {/* Waitlist form */}
              <ComingSoonWaitlist />

              {/* Meta strip */}
              <div className="grid grid-cols-3 gap-2 pt-4 border-t-2 border-[#0A0A0A]/10">
                <div>
                  <div className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>0-24M</div>
                  <div className="text-xs text-[#525252] uppercase tracking-wider">Ages</div>
                </div>
                <div>
                  <div className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>100%</div>
                  <div className="text-xs text-[#525252] uppercase tracking-wider">Cotton</div>
                </div>
                <div>
                  <div className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>ASW-TGM</div>
                  <div className="text-xs text-[#525252] uppercase tracking-wider">Mini</div>
                </div>
              </div>

              {/* Cross-shop CTA */}
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/products?category=girls" className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] px-5 py-3 text-sm font-bold hover:bg-[#FAD4D4] transition-colors" data-testid="babies-shop-girls">
                  Shop Girls
                  <ChevronLeft className="w-4 h-4 rotate-180" />
                </Link>
                <Link to="/products?category=boys" className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] px-5 py-3 text-sm font-bold hover:bg-[#D4E2FA] transition-colors" data-testid="babies-shop-boys">
                  Shop Boys
                  <ChevronLeft className="w-4 h-4 rotate-180" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#525252] hover:text-[#0A0A0A]">Home</Link>
            <span className="text-[#525252]">/</span>
            {brand ? (
              <>
                <Link to="/footwear" className="text-[#525252] hover:text-[#0A0A0A]">Footwear</Link>
                <span className="text-[#525252]">/</span>
                <span className="font-semibold">{getPageTitle()}</span>
              </>
            ) : (
              <span className="font-semibold">{getPageTitle()}</span>
            )}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to Footwear for brand pages */}
        {brand && (
          <Link to="/footwear" className="inline-flex items-center text-sm font-semibold mb-4 hover:underline" data-testid="back-to-footwear">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Footwear
          </Link>
        )}

        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {getPageTitle().toUpperCase()}
            </h1>
            <p className="text-[#525252] mt-2">{products.length} products</p>
          </div>

          {/* Mobile Filter Button */}
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                data-testid="filter-btn"
                className="mt-4 md:hidden border-2 border-[#0A0A0A] rounded-none"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-2 bg-[#0A0A0A] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full max-w-xs border-r-2 border-[#0A0A0A] p-6">
              <SheetHeader className="mb-6">
                <SheetTitle className="font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Filters
                </SheetTitle>
              </SheetHeader>
              <FilterContent />
            </SheetContent>
          </Sheet>
        </div>

        {/* Active Filters */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {category && (
              <span className="inline-flex items-center gap-2 bg-[#FAD4D4] border-2 border-[#0A0A0A] px-3 py-1 text-sm">
                {categoryOptions.find(c => c.value === category)?.label}
                <button onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('category');
                  setSearchParams(newParams);
                }}>
                  <X className="w-4 h-4" />
                </button>
              </span>
            )}
            {search && (
              <span className="inline-flex items-center gap-2 bg-[#D4E2FA] border-2 border-[#0A0A0A] px-3 py-1 text-sm">
                "{search}"
                <button onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('search');
                  setSearchParams(newParams);
                }}>
                  <X className="w-4 h-4" />
                </button>
              </span>
            )}
            {featured === 'true' && (
              <span className="inline-flex items-center gap-2 bg-[#FDF0D5] border-2 border-[#0A0A0A] px-3 py-1 text-sm">
                Featured
                <button onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('featured');
                  setSearchParams(newParams);
                }}>
                  <X className="w-4 h-4" />
                </button>
              </span>
            )}
            {promo === 'true' && (
              <span className="inline-flex items-center gap-2 bg-[#D5FDD8] border-2 border-[#0A0A0A] px-3 py-1 text-sm">
                On Sale
                <button onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('promo');
                  setSearchParams(newParams);
                }}>
                  <X className="w-4 h-4" />
                </button>
              </span>
            )}
          </div>
        )}

        <div className="flex gap-8">
          {/* Desktop Filters */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <div className="sticky top-24 border-2 border-[#0A0A0A] bg-white p-6">
              <h3 className="font-bold text-lg mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Filters
              </h3>
              <FilterContent />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="aspect-[3/4] border-2 border-[#0A0A0A] skeleton"></div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-xl font-semibold mb-2">No products found</p>
                <p className="text-[#525252] mb-6">Try adjusting your filters or search terms</p>
                <Button
                  onClick={clearFilters}
                  className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {products.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
